import React from 'react';
import { createRoot } from 'react-dom/client';
import ConfirmationPopup from '../components/ConfirmationPopup';

// Global state to track if a popup is already open
let isPopupOpen = false;
let currentRoot = null;

/**
 * Custom confirmation dialog that looks like a modern popup
 * @param {string} message - The message to display
 * @param {string} title - The title of the popup (optional)
 * @param {Object} options - Additional options (optional)
 * @returns {Promise<boolean>} - Returns true if confirmed, false if cancelled
 */
export const confirmDialog = (message, title = "Confirm Action", options = {}) => {
    return new Promise((resolve) => {
        // Prevent multiple popups
        if (isPopupOpen) {
            resolve(false);
            return;
        }

        isPopupOpen = true;

        // Create a container for the popup
        const container = document.createElement('div');
        container.id = 'custom-confirm-popup';
        document.body.appendChild(container);

        // Create React root
        currentRoot = createRoot(container);

        let notes = options.inputValue || "";
        let toggle = options.toggleValue !== undefined ? options.toggleValue : false;

        const renderPopup = () => {
            currentRoot.render(
                <ConfirmationPopup
                    isOpen={true}
                    onClose={handleCancel}
                    onConfirm={handleConfirm}
                    title={title}
                    message={message}
                    confirmText={options.confirmText || "Confirm"}
                    cancelText={options.cancelText || "Cancel"}
                    type={options.type || "default"}
                    showIcon={options.showIcon !== false}
                    className={options.className || ""}
                    showInput={options.showInput}
                    inputPlaceholder={options.inputPlaceholder}
                    inputValue={notes}
                    setInputValue={(val) => {
                        notes = val;
                        renderPopup();
                    }}
                    showToggle={options.showToggle}
                    toggleTitle={options.toggleTitle}
                    toggleValue={toggle}
                    setToggleValue={(val) => {
                        toggle = val;
                        renderPopup();
                    }}
                />
            );
        };

        const handleConfirm = () => {
            isPopupOpen = false;
            cleanup();
            if (options.showInput || options.showToggle) {
                resolve({ notes, toggle });
            } else {
                resolve(true);
            }
        };

        const handleCancel = () => {
            isPopupOpen = false;
            cleanup();
            resolve(false);
        };

        const cleanup = () => {
            if (currentRoot) {
                currentRoot.unmount();
                currentRoot = null;
            }
            if (container && container.parentNode) {
                container.parentNode.removeChild(container);
            }
        };

        renderPopup();
    });
};

/**
 * Danger confirmation dialog with red styling
 */
export const confirmDanger = (message, title = "Confirm Dangerous Action") => {
    return confirmDialog(message, title, {
        type: "danger",
        confirmText: "Delete",
        cancelText: "Cancel"
    });
};

/**
 * Warning confirmation dialog with yellow styling
 */
export const confirmWarning = (message, title = "Warning") => {
    return confirmDialog(message, title, {
        type: "warning",
        confirmText: "Proceed",
        cancelText: "Cancel"
    });
};

/**
 * Success confirmation dialog with green styling
 */
export const confirmSuccess = (message, title = "Confirm Action") => {
    return confirmDialog(message, title, {
        type: "success",
        confirmText: "Confirm",
        cancelText: "Cancel"
    });
};

/**
 * Close any open popup programmatically
 */
export const closeConfirmDialog = () => {
    if (isPopupOpen && currentRoot) {
        isPopupOpen = false;
        currentRoot.unmount();
        currentRoot = null;
        const container = document.getElementById('custom-confirm-popup');
        if (container && container.parentNode) {
            container.parentNode.removeChild(container);
        }
    }
};
